import { useId, useMemo, useState } from "react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Address, ClientAddressType, CreateClientAddressDto, UpdateClientAddressDto } from "@/lib/api";
import { FieldError, getCustomerRegistrationErrorMessage, getFieldErrors } from "@/lib/customerRegistrationError";
import { formatCep } from "@/lib/formatters";
import { AddressFormValues, EMPTY_ADDRESS_FORM } from "@/lib/validators/address";
import { AddressForm } from "./AddressForm";
import { buildShippingCopy, shouldCopyToShipping } from "./sameAddressForShipping";

interface AddressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Endereço em edição; ausente = cadastro. */
  address?: Address | null;
  /** Finalidade sugerida no cadastro (seção de onde o botão foi clicado). */
  addressType?: ClientAddressType;
  isSubmitting: boolean;
  onCreate: (address: CreateClientAddressDto) => Promise<unknown>;
  onUpdate: (addressId: number, address: UpdateClientAddressDto) => Promise<unknown>;
  /** Texto abaixo do título. O padrão descreve o espelho do endereço de cobrança no cadastro do cliente CNPJ. */
  description?: string;
  /** Mostra "Usar o mesmo endereço para entrega" no cadastro de um endereço de cobrança (cliente pessoa física). */
  allowSameForShipping?: boolean;
}

const DEFAULT_DESCRIPTION =
  "O endereço padrão de cobrança também atualiza o endereço do cadastro do cliente, usado hoje nos pedidos.";

export const SHIPPING_COPY_FAILED_MESSAGE =
  "Endereço de cobrança salvo, mas não foi possível criar o de entrega. Cadastre-o na seção de entrega.";

const toPayload = (values: AddressFormValues): UpdateClientAddressDto => ({
  postcode: values.postcode.replace(/\D/g, ""),
  street: values.street.trim(),
  number: values.number.trim(),
  district: values.district.trim(),
  complement: values.complement.trim() || undefined,
  city: values.city.trim(),
  state: values.state.toUpperCase(),
  label: values.label.trim() || undefined,
});

export function AddressDialog({
  open,
  onOpenChange,
  address,
  addressType = "Billing",
  isSubmitting,
  onCreate,
  onUpdate,
  description = DEFAULT_DESCRIPTION,
  allowSameForShipping = false,
}: AddressDialogProps) {
  const [serverErrors, setServerErrors] = useState<FieldError[]>([]);
  const [sameForShipping, setSameForShipping] = useState(false);
  const sameForShippingId = useId();
  const isEditing = !!address;

  const defaultValues = useMemo<AddressFormValues>(
    () =>
      address
        ? {
            addressType: address.addressType,
            postcode: formatCep(address.postcode),
            street: address.street,
            number: address.number ?? "",
            district: address.district ?? "",
            complement: address.complement ?? "",
            city: address.city,
            state: address.state,
            label: address.label ?? "",
            isDefault: address.isDefault,
          }
        : { ...EMPTY_ADDRESS_FORM, addressType },
    [address, addressType],
  );

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setServerErrors([]);
      setSameForShipping(false);
    }
    onOpenChange(next);
  };

  const submit = async (values: AddressFormValues) => {
    setServerErrors([]);
    try {
      if (address) {
        await onUpdate(address.id, toPayload(values));
      } else {
        const created = { ...toPayload(values), addressType: values.addressType, isDefault: values.isDefault };
        await onCreate(created);
        const copy = shouldCopyToShipping({
          enabled: allowSameForShipping,
          isEditing,
          addressType: values.addressType,
          checked: sameForShipping,
        });
        if (copy) {
          // A cobrança já foi gravada: uma falha aqui não desfaz nada e não reabre o formulário.
          await onCreate(buildShippingCopy(created)).catch(() => toast.error(SHIPPING_COPY_FAILED_MESSAGE));
        }
      }
      handleOpenChange(false);
    } catch (error) {
      setServerErrors(getFieldErrors(error));
      toast.error(getCustomerRegistrationErrorMessage(error, "Não foi possível salvar o endereço."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Editar endereço" : "Novo endereço"}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <AddressForm
          defaultValues={defaultValues}
          isEditing={isEditing}
          isSubmitting={isSubmitting}
          serverErrors={serverErrors}
          onSubmit={submit}
          onCancel={() => handleOpenChange(false)}
          renderBeforeActions={(addressType) =>
            allowSameForShipping && !isEditing && addressType === "Billing" ? (
              <div className="flex items-center gap-2">
                <Checkbox
                  id={sameForShippingId}
                  checked={sameForShipping}
                  onCheckedChange={(checked) => setSameForShipping(checked === true)}
                />
                <Label htmlFor={sameForShippingId} className="font-normal">
                  Usar o mesmo endereço para entrega
                </Label>
              </div>
            ) : null
          }
        />
      </DialogContent>
    </Dialog>
  );
}
